import { LoaderCircle } from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { fileUrl, previewUrl } from '../../api/client';
import { FILE_VIDEO_PATTERN, prepareFilePreview } from '../../api/filePreview';
import { AudioMessagePlayer } from '../../components/AudioMessagePlayer';
import { canPreviewDocument } from '../../lib/documentPreview';
import { Modal } from '../../components/Modal';
import './image-original-viewer.css';
import { TouchImagePreview } from './TouchImagePreview';

const DocumentViewer = lazy(() => import('../../components/DocumentViewer'));
const MediaViewer = lazy(() => import('../../components/MediaViewer').then(module => ({ default: module.MediaViewer })));
const IMAGE_PATTERN = /\.(png|jpe?g|gif|webp|bmp|svg|avif|tiff?|dpx)$/i;
const AUDIO_PATTERN = /\.(mp3|wav|ogg|m4a)$/i;
export const canPreviewMedia = (name: string) => IMAGE_PATTERN.test(name) || FILE_VIDEO_PATTERN.test(name);
export const canPreviewFile = (name: string) => IMAGE_PATTERN.test(name) || FILE_VIDEO_PATTERN.test(name) || AUDIO_PATTERN.test(name) || canPreviewDocument(name);
export interface PreviewSource { name: string; original: string; preview: string; download: string; allowDownload?: boolean }
type FilePreviewProps = ({ assetId: number; pathName: string; path: string } | { file: PreviewSource }) & { onClose?: () => void; embedded?: boolean; touchNavigation?: (direction:number) => void };

export function FilePreview(props: FilePreviewProps) {
  const { onClose = () => {} } = props;
  const file: PreviewSource = 'file' in props ? props.file : {
    name: props.path.split('/').at(-1) || props.path,
    original: fileUrl(props.assetId, props.pathName, props.path),
    download: fileUrl(props.assetId, props.pathName, props.path, true),
    preview: previewUrl(props.assetId, props.pathName, props.path, FILE_VIDEO_PATTERN.test(props.path) ? 1280 : 1200, FILE_VIDEO_PATTERN.test(props.path) ? 720 : 800),
  };
  const { name, original, download } = file;
  if (canPreviewDocument(name)) return <Suspense fallback={<p role="status">Открываем документ…</p>}><DocumentViewer allowDownload={file.allowDownload} file={{ name, url: original }} onClose={onClose} /></Suspense>;
  if (AUDIO_PATTERN.test(name)) return <Modal title={name} onClose={onClose} size="medium"><div className="file-audio-preview"><AudioMessagePlayer src={original} label={name} />{file.allowDownload!==false&&<a className="button button--secondary" href={download} download={name}>Скачать оригинал</a>}</div></Modal>;
  return <MediaFilePreview key={file.original} file={file} embedded={props.embedded} touchNavigation={props.touchNavigation} onClose={onClose} />;
}

function MediaFilePreview({ file, onClose, embedded, touchNavigation }: { file: PreviewSource; onClose: () => void; embedded?: boolean; touchNavigation?: (direction:number) => void }) {
  const { name: path, original, preview, download } = file;
  const video = FILE_VIDEO_PATTERN.test(path);
  // TIFF/DPX need conversion, but never use the endpoint's 200px default
  // for an opened viewer. Browser-native images retain their original bytes.
  const imagePreview = new URL(preview, window.location.href);
  imagePreview.searchParams.set('w', '4096');
  imagePreview.searchParams.set('h', '4096');
  const imagePreviewUrl = imagePreview.href;
  const [generated, setGenerated] = useState(video && /\.mov$/i.test(path));
  const [src, setSrc] = useState<string | undefined>(video ? /\.mov$/i.test(path) ? undefined : original : /\.(tiff?|dpx)$/i.test(path) ? imagePreviewUrl : original);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [label, setLabel] = useState('Загружаем превью…');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!video || !generated) return;
    const controller = new AbortController();
    void prepareFilePreview(preview, controller.signal, setLabel, attempt > 0).then(() => {
      if (!controller.signal.aborted) setSrc(preview);
    }).catch((cause: unknown) => {
      if (!controller.signal.aborted) {
        setError(cause instanceof Error ? cause.message : 'Не удалось показать превью');
        setStatus('error');
      }
    });
    return () => controller.abort();
  }, [video, generated, preview, attempt]);

  const fail = () => {
    if (!video && src !== imagePreviewUrl) setSrc(imagePreviewUrl);
    else if (video && !generated) {
      setSrc(undefined);
      setGenerated(true);
      setLabel('Готовим видео для просмотра…');
      setStatus('loading');
    } else setStatus('error');
  };
  const retry = () => {
    setError('');
    setSrc(undefined);
    setStatus('loading');
    setLabel('Готовим видео для просмотра…');
    setAttempt(value => value + 1);
  };

  if (!video && touchNavigation && status !== 'error' && src) return <TouchImagePreview key={src} src={src} name={path} onError={fail} onNavigate={touchNavigation}/>;
  if (status !== 'error' && src) return <Suspense fallback={<p role="status">Открываем…</p>}><MediaViewer key={`${src}:${attempt}`} source={{ url: src, name: path, kind: video ? 'video' : 'image', downloadUrl: download, allowDownload:file.allowDownload, playbackPrepared: video }} onClose={onClose} onMediaError={fail} embedded={embedded} /></Suspense>;

  return (
    <div className="file-preview">
      {status === 'loading' ? <div className="file-preview__status file-preview__status--waiting"><LoaderCircle className="spin" size={26} />{label}</div> : null}
      {status === 'error' ? (
        <div className="file-preview__status">
          {error || 'Не удалось показать превью'}
          {video && generated ? <button className="button button--secondary" onClick={retry} type="button">Повторить</button> : null}
          {file.allowDownload!==false&&<a className="button button--secondary" href={download} download={path}>Скачать оригинал</a>}
        </div>
      ) : null}
    </div>
  );
}
